/**
 * The IndexedDB database — the whole of this app's persistence.
 *
 * Three stores:
 *
 *   plans   one record per plan: the document (title, date, venue, settings,
 *           tables, guests) plus what surrounds it (language, version,
 *           timestamps, camera). Stored whole because the editor saves whole.
 *   assets  guest photos as Blobs. An asset id is minted once per upload and its
 *           bytes never change afterwards, which is what lets duplicates and
 *           backups share an asset by id without copying it.
 *   meta    small key → value bookkeeping (backup history).
 *
 * A schema change means a new DB version with an `upgrade` step that carries the
 * old data forward. Never break a browser that holds someone's wedding.
 *
 *   v1  the three stores
 *   v2  every plan carries `eventKind`; the plans already here were all weddings
 */

import { type DBSchema, type IDBPDatabase, openDB } from 'idb'

import type { LocaleCode } from '../i18n'
import {
  ApiError,
  type EventKind,
  type Guest,
  type PlanSettings,
  type Table,
  type Viewport,
} from '../types'

export const DB_NAME = 'yerbeyer'
export const DB_VERSION = 2

export interface StoredPlan {
  id: string
  title: string
  eventKind: EventKind
  eventDate: string | null
  venue: string | null
  language: LocaleCode
  /** Bumped by every layout save; a save based on an older one is refused. */
  version: number
  createdAt: string
  updatedAt: string
  viewport: Viewport
  settings: PlanSettings
  tables: Table[]
  /** `photoUrl` is always null here — URLs are minted per tab when read. */
  guests: Guest[]
}

export interface StoredAsset {
  id: string
  blob: Blob
  /** MIME type of `blob`. */
  type: string
  width: number
  height: number
  createdAt: string
}

interface YerbeyerSchema extends DBSchema {
  plans: { key: string; value: StoredPlan }
  assets: { key: string; value: StoredAsset }
  meta: { key: string; value: unknown }
}

export type YerbeyerDb = IDBPDatabase<YerbeyerSchema>

let opening: Promise<YerbeyerDb> | null = null

/** The open database, shared by the whole tab. */
export function getDb(): Promise<YerbeyerDb> {
  if (!opening) {
    opening = openDB<YerbeyerSchema>(DB_NAME, DB_VERSION, {
      async upgrade(db, oldVersion, _newVersion, tx) {
        if (oldVersion < 1) {
          db.createObjectStore('plans', { keyPath: 'id' })
          db.createObjectStore('assets', { keyPath: 'id' })
          db.createObjectStore('meta')
        }
        if (oldVersion < 2) {
          // Before kinds existed the app made weddings and nothing else. Only the
          // field is added: the version stays, so an editor open in another tab
          // (which `blocking` has just closed) saves on top of it as before.
          let cursor = await tx.objectStore('plans').openCursor()
          while (cursor) {
            if (!cursor.value.eventKind) await cursor.update({ ...cursor.value, eventKind: 'wedding' })
            cursor = await cursor.continue()
          }
        }
      },
      // Another tab is upgrading the schema: step aside so it can, and reopen
      // on the next call rather than holding the old version open forever.
      blocking() {
        void opening?.then((db) => db.close())
        opening = null
      },
      terminated() {
        opening = null
      },
    }).catch((error: unknown) => {
      opening = null
      throw new ApiError(503, 'storage_unavailable', `IndexedDB could not be opened: ${String(error)}`)
    })
  }
  return opening
}

/** Close and forget the connection. Tests use it to start from an empty database. */
export async function closeDb(): Promise<void> {
  const current = opening
  opening = null
  if (current) {
    try {
      ;(await current).close()
    } catch {
      /* it never opened */
    }
  }
}

/**
 * Translate a storage failure into the `ApiError` vocabulary. A full disk is
 * the one failure a person can do something about, so it gets its own code.
 */
export function asStorageError(error: unknown): unknown {
  if (error instanceof ApiError) return error
  const name = (error as { name?: string } | null)?.name
  if (name === 'QuotaExceededError') {
    return new ApiError(507, 'storage_full', 'The browser refused to store more data.')
  }
  return error
}

export const nowIso = (): string => new Date().toISOString()
